import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Sparkles, Compass } from 'lucide-react';
import { useUserAuth } from '../contexts/UserAuthContext';
import { useChannel } from '../contexts/ChannelContext';
import { personaById } from './PersonaPicker';
import { openApplyModal } from './ApplyToBeFeaturedModal';

/**
 * PERSONA_CONTENT_MAP — the source of truth for what each persona cares
 * about. Used everywhere we tailor the experience:
 *   • Which story categories get prioritized in the feed
 *   • Which homepage audience-card is highlighted
 *   • What CTA the user sees at the top of the page
 *   • What inquiry-type the contact form opens with
 *
 * The match is intentionally loose — empty arrays mean "show everything",
 * and we always fall back to the full feed if the filter empties it out.
 * That way old stories without explicit audience tags still surface.
 */
export const PERSONA_CONTENT_MAP = {
  athlete: {
    priorityCategories: ['MLB', 'NFL', 'NBA', 'WNBA', 'NHL', 'MLS', 'College', 'High School', 'Draft'],
    leagueAudienceFilter: 'athletes',         // which homepage card to spotlight
    visionaryAudienceFilter: null,
    flytFocus: 'voices',
    cta: {
      eyebrow: 'For athletes',
      headline: 'Your story, your terms.',
      action: 'Apply to be featured',
      to: '/athletes',
      flytTo: '/flyt/process',
      visionaryTo: '/visionary/contact?type=Pioneer',
    },
  },
  brand: {
    priorityCategories: ['Brand', 'Partnership', 'Campaign'],
    leagueAudienceFilter: 'brands',
    visionaryAudienceFilter: 'ceos',
    flytFocus: 'brands',
    cta: {
      eyebrow: 'For brands',
      headline: 'See the athletes, see the audience.',
      action: 'Partnership packages',
      to: '/brands',
      flytTo: '/flyt/brands',
      visionaryTo: '/visionary/contact?type=CEO',
    },
  },
  investor: {
    priorityCategories: ['Founder', 'Business', 'CEO', 'Entrepreneur'],
    leagueAudienceFilter: 'brands',
    visionaryAudienceFilter: 'ceos',
    flytFocus: 'voices',
    cta: {
      eyebrow: 'For investors',
      headline: 'Capital with conviction.',
      action: 'Founder stories on Dreamers',
      to: '/visionary',
      flytTo: '/visionary',
      visionaryTo: '/visionary/stories',
    },
  },
  school: {
    priorityCategories: ['College', 'High School', 'Draft', 'Recruiting'],
    leagueAudienceFilter: 'programs',
    visionaryAudienceFilter: null,
    flytFocus: 'voices',
    cta: {
      eyebrow: 'For programs',
      headline: 'Recruit through stories, not stats.',
      action: 'Program packages',
      to: '/programs',
      flytTo: '/flyt/process',
      visionaryTo: '/visionary/contact?type=Pioneer',
    },
  },
  fan: {
    // Fans see everything — they're here for the storytelling, not a niche.
    priorityCategories: [],
    leagueAudienceFilter: null,
    visionaryAudienceFilter: null,
    flytFocus: null,
    cta: {
      eyebrow: 'For fans',
      headline: 'Stories that take you behind the highlights.',
      action: 'Browse all stories',
      to: '/stories',
      flytTo: '/flyt/stories',
      visionaryTo: '/visionary/stories',
    },
  },
};

const STORAGE_KEY = (uid) => `dl_show_everything_${uid}`;

/**
 * Hook that returns:
 *   • personaId       — current persona ('athlete'|'brand'|'investor'|'school'|'fan'|null)
 *   • personaMeta     — the full PERSONA record (label, color, icon, etc.)
 *   • config          — PERSONA_CONTENT_MAP entry for this persona
 *   • isTailoring     — true when we should actively filter content
 *   • showEverything  — user toggled "Show everything" → don't filter
 *   • setShowEverything(bool) — flip the per-user opt-out
 *   • filterStories(stories) — prioritize stories matching personaCategories
 *   • filterAudienceCards(cards) — keep only the relevant card (or all)
 */
export const usePersonaTailoring = () => {
  const { user } = useUserAuth();
  const channel = useChannel();
  const personaId = user?.persona || null;
  const personaMeta = personaById(personaId);
  const config = personaId ? PERSONA_CONTENT_MAP[personaId] : null;

  // Read the per-user "Show everything" pref synchronously on the initial
  // render so we don't flash the wrong tailoring state for one frame.
  // Subsequent user changes (login/logout) re-key via the second arg.
  const [showEverything, setShowEverythingState] = useState(() => {
    if (!user) return false;
    try { return localStorage.getItem(STORAGE_KEY(user.user_id)) === '1'; }
    catch { return false; }
  });
  // When the signed-in user actually changes, re-read the preference.
  const userKey = user?.user_id || null;
  const [currentKey, setCurrentKey] = useState(userKey);
  if (currentKey !== userKey) {
    setCurrentKey(userKey);
    let next = false;
    if (userKey) {
      try { next = localStorage.getItem(STORAGE_KEY(userKey)) === '1'; }
      catch { /* ignore */ }
    }
    setShowEverythingState(next);
  }

  const setShowEverything = (v) => {
    setShowEverythingState(v);
    if (!user) return;
    try { localStorage.setItem(STORAGE_KEY(user.user_id), v ? '1' : '0'); } catch { /* ignore */ }
  };

  // Tailoring is "on" whenever the user has any persona AND hasn't opted
  // out. Fans don't get *content* filtering (empty priorityCategories) but
  // they DO get their persona panel (the native ads surface), so we keep
  // them inside the tailoring envelope.
  const isTailoring = Boolean(personaId && config && !showEverything);

  // Filter strategy: if a persona has priority categories, keep matches first,
  // followed by the rest. Never return an empty array — fall back to full
  // list if no story matches (so the homepage never goes blank).
  const filterStories = (stories) => {
    if (!isTailoring || !config.priorityCategories.length) return stories;
    const wanted = new Set(config.priorityCategories.map((c) => c.toLowerCase()));
    const matches = stories.filter((s) => s?.category && wanted.has(s.category.toLowerCase()));
    if (matches.length === 0) return stories;
    const rest = stories.filter((s) => !matches.includes(s));
    return [...matches, ...rest];
  };

  // Audience-card filter — used by the "One Platform / Every Visionary"
  // grid on the homepage. When tailoring, keep ONLY the card matching the
  // user's persona; when fan or untailored, show all four cards.
  const filterAudienceCards = (cards) => {
    if (!isTailoring) return cards;
    const wanted = channel.id === 'dreams_visionary'
      ? config.visionaryAudienceFilter
      : config.leagueAudienceFilter;
    if (!wanted) return cards;
    return cards.filter((c) => c.testid && c.testid.endsWith(`-${wanted}`));
  };

  const ctaTo = (() => {
    if (!config) return null;
    if (channel.id === 'flyt') return config.cta.flytTo;
    if (channel.id === 'dreams_visionary') return config.cta.visionaryTo;
    return config.cta.to;
  })();

  return {
    personaId, personaMeta, config,
    isTailoring, showEverything, setShowEverything,
    filterStories, filterAudienceCards,
    ctaTo,
  };
};

/**
 * PersonaFocusBanner — slim contextual strip that sits at the very top of
 * the homepage when a persona is active. Tells the user the feed is being
 * tailored for them + gives them an obvious escape hatch.
 */
export const PersonaFocusBanner = () => {
  const { personaMeta, isTailoring, showEverything, setShowEverything, config, ctaTo, personaId } = usePersonaTailoring();
  if (!personaMeta || !config) return null;
  // iter128 · Athletes have a dedicated "GET FEATURED — APPLY FOR A
  // STORY" panel (see PersonaActionPanel → AthletePanel) that already
  // carries the same tailored intent. Rendering this focus banner
  // AND the CTAStrip AND the AthletePanel back-to-back created three
  // stacked athlete-only strips on the homepage — pure duplication.
  // Skip the top banner entirely for the athlete persona; the panel
  // below is the single source of truth for their apply-CTA.
  if (personaId === 'athlete') return null;

  // Always render — when showEverything is on, the banner becomes the "turn
  // tailoring back on" CTA instead of hiding entirely.
  const Icon = personaMeta.Icon;
  const isAthleteApply = personaId === 'athlete';

  return (
    <div
      className={`${personaMeta.accent} text-white`}
      data-testid="persona-focus-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3 flex-wrap">
        <Icon className="w-4 h-4 flex-shrink-0" />
        <p className="text-[12px] sm:text-sm font-bold uppercase tracking-wider flex-1 min-w-0">
          {isTailoring ? (
            <>Tailored for <span className="underline underline-offset-4">{personaMeta.label}s</span></>
          ) : (
            <>Tailoring paused · showing everything</>
          )}
        </p>
        {ctaTo && isTailoring && (
          isAthleteApply ? (
            <button
              type="button"
              onClick={openApplyModal}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/90 text-black text-[11px] font-bold uppercase tracking-wider hover:bg-white"
              data-testid="persona-banner-cta"
            >
              <Sparkles className="w-3 h-3" /> {config.cta.action}
            </button>
          ) : (
            <Link
              to={ctaTo}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/90 text-black text-[11px] font-bold uppercase tracking-wider hover:bg-white"
              data-testid="persona-banner-cta"
            >
              <Sparkles className="w-3 h-3" /> {config.cta.action}
            </Link>
          )
        )}
        <button
          type="button"
          onClick={() => setShowEverything(!showEverything)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/30 hover:bg-black/50 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider"
          data-testid="persona-toggle-everything"
        >
          {showEverything ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          {showEverything ? 'Tailor for me' : 'Show everything'}
        </button>
      </div>
    </div>
  );
};

/**
 * PersonaCTAStrip — a 1-row CTA banner placed near the top of any page
 * (homepage hero or stories page) that surfaces the most relevant next
 * action for the current persona. Renders nothing if there's no persona
 * or the user opted out.
 */
export const PersonaCTAStrip = () => {
  const { personaMeta, config, isTailoring, ctaTo, personaId } = usePersonaTailoring();
  if (!isTailoring || !personaMeta || !config || !ctaTo) return null;
  // iter128 · Same rationale as PersonaFocusBanner — the athlete
  // persona already gets a dedicated apply CTA inside AthletePanel.
  // A second one right above the panel was pure duplication.
  if (personaId === 'athlete') return null;

  const Icon = personaMeta.Icon;
  const isAthleteApply = personaId === 'athlete';
  return (
    <section className="bg-[#f7f6f3] border-y border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
        <span className={`w-10 h-10 rounded-full ${personaMeta.accent} text-white flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-gray-500">{config.cta.eyebrow}</p>
          <p className="font-display text-base sm:text-lg uppercase leading-tight truncate">{config.cta.headline}</p>
        </div>
        {isAthleteApply ? (
          <>
            <button
              type="button"
              onClick={openApplyModal}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-gray-800"
              data-testid="persona-cta-action"
            >
              <Compass className="w-3.5 h-3.5" /> {config.cta.action}
            </button>
            <button
              type="button"
              onClick={openApplyModal}
              className="sm:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black text-white text-[11px] font-bold uppercase tracking-wider"
              data-testid="persona-cta-action-mobile"
            >
              Go
            </button>
          </>
        ) : (
          <>
            <Link
              to={ctaTo}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-gray-800"
              data-testid="persona-cta-action"
            >
              <Compass className="w-3.5 h-3.5" /> {config.cta.action}
            </Link>
            <Link
              to={ctaTo}
              className="sm:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black text-white text-[11px] font-bold uppercase tracking-wider"
              data-testid="persona-cta-action-mobile"
            >
              Go
            </Link>
          </>
        )}
      </div>
    </section>
  );
};
